import { Dispatch, FC, SetStateAction, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { generatePath, Link } from 'react-router'

import { CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Button, Flex, Modal, Popover, Table, theme, Typography } from 'antd'
import { ColumnType } from 'antd/es/table'

import { Country } from '../../components/icon/Country'
import { useResultsQuery } from '../../graphql'
import routes from '../../routes'

import { Context } from './Context'
import { RiderNumber } from './RiderNumber'

type ColumnProps = Partial<RiderCardFragment> & {
  documentId: string
  win_rate: number
  world_championship_wins: number
  points_total: number
  dnf: number
}

const columns: ColumnType<ColumnProps>[] = [
  {
    key: 'rider.country',
    dataIndex: ['country'],
    title: <FormattedMessage id={'rider.country'} />,
    render: (country: string) => <Country country={country} />,
    align: 'center',
  },
  {
    key: 'rider.name',
    title: <FormattedMessage id={'rider.name'} />,
    fixed: 'left',
    render: (value: RiderCardFragment) => (
      <Flex>
        <RiderNumber number={value.riderNumber} />
        <Typography.Text ellipsis>
          <Link to={generatePath(routes.rider, { slug: value.slug })}>{value.name}</Link>
        </Typography.Text>
      </Flex>
    ),
  },
  {
    key: 'rider.total-races',
    dataIndex: ['stats', 'all_races', 'total'],
    title: <FormattedMessage id={'rider.total-races'} />,
    sorter: (a, b) => a.stats?.all_races?.total - b.stats?.all_races?.total,
    align: 'center',
  },
  {
    key: 'rider.total-points',
    dataIndex: ['points_total'],
    title: <FormattedMessage id={'rider.total-points'} />,
    sorter: (a, b) => a.points_total - b.points_total,
    align: 'center',
  },
  {
    key: 'rider.total-poles',
    dataIndex: ['stats', 'poles', 'total'],
    title: <FormattedMessage id={'rider.total-poles'} />,
    sorter: (a, b) => a.stats?.poles?.total - b.stats?.poles?.total,
    align: 'center',
  },
  {
    key: 'rider.total-podiums',
    dataIndex: ['stats', 'podiums', 'total'],
    title: <FormattedMessage id={'rider.total-podiums'} />,
    sorter: (a, b) => a.stats?.podiums?.total - b.stats?.podiums?.total,
    align: 'center',
  },
  {
    key: 'rider.total-gran-prix-victories',
    dataIndex: ['stats', 'grand_prix_victories', 'total'],
    title: (
      <Flex gap={8} align={'center'} justify={'center'}>
        <FormattedMessage id={'rider.total-gran-prix-victories'} />
        <Popover content={<FormattedMessage id={'rider.gran-prix-victories.info'} />}>
          <InfoCircleOutlined />
        </Popover>
      </Flex>
    ),
    sorter: (a, b) => a.stats?.grand_prix_victories?.total - b.stats?.grand_prix_victories?.total,
    render: (value: number, record) => `${value} (${record.win_rate}%)`,
    align: 'center',
  },
  {
    key: 'rider.total-world-championship-victories',
    dataIndex: ['world_championship_wins'],
    title: (
      <Flex gap={8} align={'center'} justify={'center'}>
        <FormattedMessage id={'rider.total-world-championship-victories'} />
        <Popover content={<FormattedMessage id={'rider.total-world-championship-victories.info'} />}>
          <InfoCircleOutlined />
        </Popover>
      </Flex>
    ),
    sorter: (a, b) => a.world_championship_wins - b.world_championship_wins,
    align: 'center',
  },
  {
    key: 'rider.dnf',
    dataIndex: ['dnf'],
    title: (
      <Flex gap={8} align={'center'} justify={'center'}>
        <FormattedMessage id={'rider.dnf'} />
        <Popover content={<FormattedMessage id={'rider.dnf.info'} />}>
          <InfoCircleOutlined />
        </Popover>
      </Flex>
    ),
    sorter: (a, b) => a.dnf - b.dnf,
    render: (value: number, record) => `${value} (${Math.floor((value / record.stats?.all_races.total) * 100)}%)`,
    align: 'center',
  },
]

const ComparingModal: FC<{ open: boolean; setOpen: Dispatch<SetStateAction<boolean>> }> = ({ open, setOpen }) => {
  const { data, checked, setChecked } = useContext(Context)
  const { token } = theme.useToken()
  const results = useResultsQuery({
    variables: { filters: { rider: { slug: { in: checked } }, didNotFinish: { eq: true } } },
  })
  const { formatMessage } = useIntl()
  return (
    <Modal
      title={formatMessage({ id: 'rider.compare.title' })}
      open={open}
      onCancel={() => setOpen(false)}
      onClose={() => setOpen(false)}
      footer={null}
      width={1400}
    >
      <Table
        size={'small'}
        style={{ overflow: 'scroll' }}
        onRow={() => ({ style: { backgroundColor: token.colorBgContainer } })}
        showHeader={true}
        rowKey={(it) => `${it?.documentId}`}
        pagination={{ hideOnSinglePage: true }}
        dataSource={checked
          ?.map((it) => data?.find((r) => r?.slug === it))
          .map((it) => {
            return {
              ...it,
              documentId: `${it?.documentId}`,
              win_rate: Math.floor((it?.stats?.grand_prix_victories?.total / it?.stats?.all_races?.total) * 100),
              points_total: (it?.statistics as MotoGP.RiderStatistics[])?.reduce((all, each) => all + each.points, 0),
              world_championship_wins: (it?.stats?.world_championship_wins?.categories as { count: number }[])?.reduce(
                (all, it) => all + it.count,
                0,
              ),
              dnf: results.data?.results?.filter((r) => r?.rider?.documentId === it?.documentId).length ?? 0,
            }
          })}
        columns={columns.concat({
          key: 'action',
          render: (it: ColumnProps) => (
            <Button
              type={'link'}
              size={'small'}
              onClick={() => setChecked((prevState) => prevState.filter((i) => i !== it.slug))}
              icon={<CloseCircleOutlined style={{ color: token.colorError }} />}
            />
          ),
        })}
      />
    </Modal>
  )
}

export { ComparingModal }
